/**
 * 智能剪辑节点卡片内容（v0.33 样式对齐设计稿「2.连接素材节点状态」，方案 §4.1.2）
 * 空态：剪刀线条图标 + 单行提示；
 * 连接素材态/已保存态：封面全出血（上游视频首帧或导出缩略图）+ 中央剪刀与「打开智能剪辑」
 * 浅色小按钮 + 封面底部内嵌迷你时间线缩略条（时间刻度 + 轨道示意条，纯装饰）。
 */
import React, { memo } from 'react';
import { Scissors } from 'lucide-react';
import { AppNode } from '@/types';
import { EditDocument } from '@/types/smartEdit';
import { documentDuration } from '@/utils/timelineLayout';

interface SmartEditContentProps {
  data: AppNode;
  onOpenSmartEdit?: (nodeId: string) => void;
  /** 连接素材态：首个上游视频（封面取首帧、迷你时间线取真实时长） */
  upstreamVideo?: { videoUri: string; durationSec?: number } | null;
}

/** 迷你时间线缩略条：与卡片左右/底边平齐的通栏底条，11 个时间刻度 + 两条轨道示意条（纯装饰） */
const MiniTimelineRuler: React.FC<{ durationSec?: number }> = ({ durationSec }) => {
  const total = durationSec && durationSec > 0 ? durationSec : 72;
  const steps = 10;
  // 刻度间隔取整（1/2/5/10/15/30/60s 阶梯），保证 11 个标签逐格递增不重复（参考图同款均匀刻度）
  const raw = total / steps;
  const interval = raw <= 1 ? 1 : raw <= 2 ? 2 : raw <= 5 ? 5 : raw <= 10 ? 10
    : raw <= 15 ? 15 : raw <= 30 ? 30 : raw <= 60 ? 60 : Math.ceil(raw / 60) * 60;
  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.round(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };
  return (
    <div
      className="absolute left-0 right-0 bottom-0 z-10 bg-[#161616] px-3 pt-2 pb-2.5 pointer-events-none select-none"
      data-testid="smart-mini-timeline"
    >
      <div className="flex justify-between">
        {Array.from({ length: steps + 1 }, (_, i) => (
          <span key={i} className="text-[10px] leading-none text-white/35 tabular-nums">
            {fmt(interval * i)}
          </span>
        ))}
      </div>
      <div className="mt-1.5 space-y-1.5">
        <div className="h-5 rounded-[3px] bg-white/20" />
        <div className="h-5 rounded-[3px] bg-white/10" />
      </div>
    </div>
  );
};

const SmartEditContentComponent: React.FC<SmartEditContentProps> = memo(({
  data,
  onOpenSmartEdit,
  upstreamVideo,
}) => {
  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onOpenSmartEdit?.(data.id);
  };

  const d = data.data as Record<string, unknown>;
  const thumbnail = d?.thumbnailDataUrl as string | undefined;
  // 已保存态时长从剪辑文档算；连接素材态用上游视频元数据时长（迷你时间线刻度用）
  const savedDuration = thumbnail && d?.editDocument ? documentDuration(d.editDocument as EditDocument) : undefined;
  const durationSec = thumbnail ? savedDuration : upstreamVideo?.durationSec;
  // 封面来源：自己导出的缩略图 > 上游视频首帧
  const coverSrc = thumbnail ?? upstreamVideo?.videoUri;

  // 连接素材态/已保存态（设计稿「2.连接素材节点状态」）：封面全出血 + 中央入口 + 迷你时间线
  if (coverSrc) {
    return (
      <div className="relative w-full h-full overflow-hidden bg-black" onDoubleClick={handleOpen}>
        {thumbnail ? (
          <img
            src={coverSrc}
            alt="智能剪辑封面"
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          <video
            src={coverSrc}
            muted
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {/* 暗色遮罩：压暗视频封面，突显中央图标/按钮与时间线（参考图同款） */}
        <div className="absolute inset-0 bg-black/40" />
        {/* 中央：剪刀 + 打开智能剪辑（浅色小按钮，设计稿同款比例） */}
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 pb-12">
          <Scissors size={30} strokeWidth={1.7} className="text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" />
          <button
            type="button"
            onClick={handleOpen}
            onMouseDown={(e) => e.stopPropagation()}
            className="px-5 py-2.5 rounded-lg bg-zinc-200/90 hover:bg-white text-zinc-900 text-[14px] font-medium leading-none shadow-lg transition-colors"
            title="打开智能剪辑"
            data-testid="smart-open-btn"
          >
            打开智能剪辑
          </button>
        </div>
        <MiniTimelineRuler durationSec={durationSec} />
      </div>
    );
  }

  // 空态（设计稿对齐）：剪刀线条图标 + 单行提示
  return (
    <div
      className="relative flex flex-col items-center justify-center h-full px-10 text-center"
      onDoubleClick={handleOpen}
    >
      <Scissors size={52} strokeWidth={1.1} className="text-white/30 mb-4" />
      <p className="text-[13px] text-white/40">空空如也，请连接视频节点后操作</p>
    </div>
  );
});

SmartEditContentComponent.displayName = 'SmartEditContent';
export const SmartEditContent = SmartEditContentComponent;
